import React, { useState, useRef, useEffect } from 'react';
import { MoreHorizontal, Plus, X, Copy } from 'lucide-react';
import noBrandImage from '../../assets/img/brand/no-brand-img.png';
import emptySearchImage from '../../assets/img/brand/empty-search.png';
import { useHistory, useLocation } from 'react-router-dom';
import apiHelper from '../../apiHelper';
import AlertNotification from '../../components/AlertNotification';
import DiscardConfirmationModal from '../../components/DiscardConfirmationModal';
import { useLoader } from '../../components/GlobalLoader';
import { Routes } from '../../routes';
import Pagination from '../../components/Pagination';
import SearchBox from '../../components/SearchBox';
import SortableHeader from '../../components/SortableHeader';
import FinalResultRuleTooltip from '../../components/FinalResultRuleTooltip.js';
import { finalResultRuleLabel, normalizeFinalResultRule } from '../../utils/finalResultRule.js';
import { FEE_FLAT, FEE_PCT_PLATFORM, normalizeFees, normalizeMarkups, formatMarkupRange, formatMarkupValue } from '../../utils/pricingTemplate.js';

const PAGE_SIZE = 10;

const formatFeeDisplay = (row) => {
    if (!row) return '-';
    if (row.formula_type === FEE_FLAT) return row.value ? `SGD ${parseFloat(row.value).toFixed(2)}` : '-';
    return row.value ? `${parseFloat(parseFloat(row.value).toFixed(5)).toString()}%` : '-';
};

export default function PricingTemplate() {
    const history = useHistory();
    const location = useLocation();
    const breadcrumbs = ["Settings", "Pricing Template"];
    const { showLoader, hideLoader } = useLoader();

    const [activeTab, setActiveTab] = useState("active");

    const [templates, setTemplates] = useState([]);
    const [archivedTemplates, setArchivedTemplates] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [archivedTotalCount, setArchivedTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [archivedPage, setArchivedPage] = useState(1);

    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [archivedSearchInput, setArchivedSearchInput] = useState('');
    const [archivedSearchTerm, setArchivedSearchTerm] = useState('');

    const [sortField, setSortField] = useState('modified_at');
    const [sortOrder, setSortOrder] = useState('desc');
    const [archivedSortField, setArchivedSortField] = useState('archived_at');
    const [archivedSortOrder, setArchivedSortOrder] = useState('desc');

    // Table row dropdown
    const [selectedRow, setSelectedRow] = useState(null);
    const [dropdownOpenId, setDropdownOpenId] = useState(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
    const dropdownRef = useRef(null);
    const buttonRefs = useRef({});

    // Drawer
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerTab, setDrawerTab] = useState('general');
    const drawerRef = useRef(null);

    // Drawer dots dropdown
    const [drawerDotsOpen, setDrawerDotsOpen] = useState(false);
    const drawerDotsRef = useRef(null);
    // Number each list / drawer request, so a slower older response can't replace a newer one
    const listLoadSeq = useRef(0);
    const drawerLoadSeq = useRef(0);

    // Action modals
    const [deactivateModal, setDeactivateModal] = useState(false);
    const [archiveModal, setArchiveModal] = useState(false);

    // Copy ID state
    const [copied, setCopied] = useState(false);
    const handleCopyId = (id) => {
        navigator.clipboard.writeText(id);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const [alert, setAlert] = useState({ show: false, type: '', message: '' });

    const [colWidths, setColWidths] = useState({
        templateName: 220,
        platformFees: 300,
        platformMarkup: 280,
        finalResultRule: 190,
        dateCol: 180,
    });

    const handleColResizeMouseDown = (e, key, startWidth, minWidth = 120) => {
        e.preventDefault();
        e.stopPropagation();
        const startX = e.clientX;
        const onMouseMove = (moveEvent) => {
            const delta = moveEvent.clientX - startX;
            setColWidths((prev) => ({ ...prev, [key]: Math.max(minWidth, startWidth + delta) }));
        };
        const onMouseUp = () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    };

    const ColResizeHandle = ({ colKey, minWidth }) => (
        <div
            className="col-resize-handle"
            onMouseDown={(e) => handleColResizeMouseDown(e, colKey, colWidths[colKey], minWidth)}
            onClick={(e) => e.stopPropagation()}
            style={{ position: 'absolute', right: 0, top: 0, height: '100%', width: '6px', cursor: 'col-resize', zIndex: 2, userSelect: 'none' }}
        />
    );

    // Show success banner when navigating back from Add/Edit
    useEffect(() => {
        if (location.state?.success) {
            setAlert({ show: true, type: 'success', message: location.state.success });
            setTimeout(() => setAlert({ show: false, type: '', message: '' }), 4000);
            history.replace(location.pathname, {});
        }
    }, []);

    const isActive = activeTab === 'active';
    const data = isActive ? templates : archivedTemplates;
    const total = isActive ? totalCount : archivedTotalCount;
    const page = isActive ? currentPage : archivedPage;
    const setPage = isActive ? setCurrentPage : setArchivedPage;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const activeSortField = isActive ? sortField : archivedSortField;
    const activeSortOrder = isActive ? sortOrder : archivedSortOrder;
    const setActiveSortField = isActive ? setSortField : setArchivedSortField;
    const setActiveSortOrder = isActive ? setSortOrder : setArchivedSortOrder;

    const showAlert = (type, message) => {
        setAlert({ show: true, type, message });
        setTimeout(() => setAlert({ show: false, type: '', message: '' }), 4000);
    };

    const formatDateTime = (isoString) => {
        if (!isoString) return '';
        const normalized = isoString.endsWith('Z') || isoString.includes('+') ? isoString : isoString + 'Z';
        const date = new Date(normalized);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}`;
    };

    const normalizeTemplate = (t) => ({
        ...t,
        name:               t.template_name            || t.name               || '',
        final_result_rule:  normalizeFinalResultRule(t.final_result_rule),
        open_id:            t.uuid                     || t.open_id            || '',
        created_by:         t.created_by_full_name     || t.created_by_name    || t.created_by    || '',
        created_time:       t.created_at               || t.created_time       || '',
        last_modified_by:   t.modified_by_full_name    || t.modified_by_name   || t.modified_by   || t.last_modified_by || '',
        last_modified_time: t.modified_at              || t.updated_at         || t.last_modified_time || '',
        archived_by:        t.archived_by_full_name    || t.archived_by_name   || t.archived_by   || '',
        archived_time:      t.archived_at              || t.archived_time      || '',
        rows: normalizeFees(t.fees || t.rows || []),
        markups: normalizeMarkups(t.markups),
    });

    const fetchTemplates = async () => {
        const seq = ++listLoadSeq.current;
        showLoader();
        try {
            const p = isActive ? currentPage : archivedPage;
            const s = isActive ? searchTerm : archivedSearchTerm;
            const params = new URLSearchParams({
                page: p,
                page_size: PAGE_SIZE,
                ...(s && { search: s }),
                ...(activeSortField && { ordering: `${activeSortOrder === 'desc' ? '-' : ''}${activeSortField}` }),
                ...(!isActive && { status: 'archived' }),
            });
            const res = await apiHelper(`sales/pricing-templates/?${params}`, 'GET');
            // A newer tab/page/search/sort request has started; drop this response
            if (seq !== listLoadSeq.current) return;
            if (!res.ok) { showAlert('danger', 'Failed to load pricing templates.'); return; }
            const json = await res.json();
            if (seq !== listLoadSeq.current) return;
            if (isActive) {
                setTemplates((json.results || []).map(normalizeTemplate));
                setTotalCount(json.count || 0);
            } else {
                setArchivedTemplates((json.results || []).map(normalizeTemplate));
                setArchivedTotalCount(json.count || 0);
            }
        } catch (e) {
            if (seq === listLoadSeq.current) showAlert('danger', 'Failed to load pricing templates.');
        } finally {
            hideLoader();
        }
    };

    useEffect(() => {
        fetchTemplates();
    }, [activeTab, page, searchTerm, archivedSearchTerm, sortField, sortOrder, archivedSortField, archivedSortOrder]);

    // Close drawer on outside click
    useEffect(() => {
        function handleClickOutsideDrawer(e) {
            if (deactivateModal || archiveModal) return;
            if (drawerOpen && drawerRef.current && !drawerRef.current.contains(e.target)) {
                setDrawerOpen(false);
                setDrawerDotsOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutsideDrawer);
        return () => document.removeEventListener('mousedown', handleClickOutsideDrawer);
    }, [drawerOpen, deactivateModal, archiveModal]);

    // Close drawer dots on outside click
    useEffect(() => {
        function handleClickOutside(e) {
            if (drawerDotsOpen && drawerDotsRef.current && !drawerDotsRef.current.contains(e.target)) {
                setDrawerDotsOpen(false);
            }
        }
        if (drawerDotsOpen) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [drawerDotsOpen]);

    // Close table row dropdown on outside click
    useEffect(() => {
        function handleClickOutside(e) {
            if (
                dropdownOpenId !== null &&
                dropdownRef.current &&
                !dropdownRef.current.contains(e.target) &&
                !buttonRefs.current[dropdownOpenId]?.contains(e.target)
            ) {
                setDropdownOpenId(null);
            }
        }
        if (dropdownOpenId !== null) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [dropdownOpenId]);

    const toggleDropdown = (id) => {
        if (dropdownOpenId === id) {
            setDropdownOpenId(null);
            return;
        }
        const btn = buttonRefs.current[id];
        if (btn) {
            const rect = btn.getBoundingClientRect();
            const dropdownWidth = 170;
            const dropdownHeight = 160;
            let left = rect.left + window.scrollX + rect.width - dropdownWidth;
            if (left < window.scrollX + 8) left = window.scrollX + 8;
            let top = rect.bottom + window.scrollY;
            if (top + dropdownHeight > window.scrollY + window.innerHeight) {
                top = rect.top + window.scrollY - dropdownHeight;
            }
            setDropdownPosition({ top, left });
        }
        setDropdownOpenId(id);
    };

    const handleSort = (field) => {
        if (activeSortField === field) {
            setActiveSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setActiveSortField(field);
            setActiveSortOrder('asc');
        }
        setPage(1);
    };

    const openDeactivateModal = (template) => {
        setSelectedRow(template);
        setDropdownOpenId(null);
        setDrawerDotsOpen(false);
        setDeactivateModal(true);
    };

    const openArchiveModal = (template) => {
        setSelectedRow(template);
        setDropdownOpenId(null);
        setDrawerDotsOpen(false);
        setArchiveModal(true);
    };

    const handleDeactivateConfirm = async () => {
        try {
            const res = await apiHelper(`sales/pricing-templates/update-status/${selectedRow.pricing_template_id}/`, 'POST', { status: 'inactive' });
            if (!res.ok) { const json = await res.json(); showAlert('danger', json.error || 'Failed to deactivate.'); return; }
            setDeactivateModal(false);
            showAlert('success', 'Pricing Template Deactivated Successfully');
            fetchTemplates();
            const detail = await apiHelper(`sales/pricing-templates/${selectedRow.pricing_template_id}/`, 'GET');
            if (detail.ok) setSelectedRow(normalizeTemplate(await detail.json()));
        } catch {
            showAlert('danger', 'Failed to deactivate. Please try again.');
        }
    };

    const handleArchiveConfirm = async () => {
        try {
            const res = await apiHelper(`sales/pricing-templates/${selectedRow.pricing_template_id}/archive/`, 'POST');
            if (!res.ok) { const json = await res.json(); showAlert('danger', json.error || 'Failed to archive.'); return; }
            setArchiveModal(false);
            setDrawerOpen(false);
            showAlert('success', 'Pricing Template Archived Successfully');
            fetchTemplates();
        } catch {
            showAlert('danger', 'Failed to archive. Please try again.');
        }
    };

    const handleActivate = async (template) => {
        try {
            const res = await apiHelper(`sales/pricing-templates/update-status/${template.pricing_template_id}/`, 'POST', { status: 'active' });
            if (!res.ok) { const json = await res.json(); showAlert('danger', json.error || 'Failed to activate.'); return; }
            setDropdownOpenId(null);
            setDrawerDotsOpen(false);
            showAlert('success', 'Pricing Template Activated Successfully');
            fetchTemplates();
            const detail = await apiHelper(`sales/pricing-templates/${template.pricing_template_id}/`, 'GET');
            if (detail.ok) setSelectedRow(normalizeTemplate(await detail.json()));
        } catch {
            showAlert('danger', 'Failed to activate. Please try again.');
        }
    };

    const openDrawer = async (template) => {
        setSelectedRow(template);
        setDrawerTab('general');
        setDrawerOpen(true);
        setDropdownOpenId(null);
        const seq = ++drawerLoadSeq.current;
        try {
            const res = await apiHelper(`sales/pricing-templates/${template.pricing_template_id}/`, 'GET');
            if (!res.ok) return;
            const json = await res.json();
            // Another template was opened meanwhile; don't show this one's details in its drawer
            if (seq !== drawerLoadSeq.current) return;
            setSelectedRow(normalizeTemplate(json));
        } catch { /* keep list-row data */ }
    };

    const closeDrawer = () => {
        setDrawerOpen(false);
        setDrawerDotsOpen(false);
    };

    return (
        <div className="company-body new-look stock-wrap">
            {/* Breadcrumbs */}
            <div className="breadcrumb">
                {breadcrumbs.map((crumb, index) => (
                    <span key={index} className="breadcrumb-item">
                        {crumb}
                    </span>
                ))}
            </div>

            {alert.show && (
                <AlertNotification
                    type={alert.type}
                    message={alert.message}
                    onClose={() => setAlert({ show: false, type: '', message: '' })}
                />
            )}

            {/* Tabs */}
            <div className="employee-tabs-container">
                <div className="tabs">
                    <button
                        className={`tab ${activeTab === 'active' ? 'active' : ''}`}
                        onClick={() => { setActiveTab('active'); setDropdownOpenId(null); }}
                    >
                        Pricing Templates
                    </button>
                    <button
                        className={`tab ${activeTab === 'archived' ? 'active' : ''}`}
                        onClick={() => { setActiveTab('archived'); setDropdownOpenId(null); }}
                    >
                        Archived Pricing Templates
                    </button>
                </div>

                <div className="info-section">
                    <div className="info-card" style={{ overflow: 'unset' }}>
                        <div className="entities-tab">

                            {/* Toolbar */}
                            <div className="toolbar">
                                <div className="toolbar-left">
                                    <SearchBox
                                        searchInput={isActive ? searchInput : archivedSearchInput}
                                        setSearchInput={isActive ? setSearchInput : setArchivedSearchInput}
                                        setSearchTerm={isActive ? setSearchTerm : setArchivedSearchTerm}
                                        setCurrentPage={setPage}
                                        disabled={isActive ? (totalCount === 0 && !searchTerm) : (archivedTotalCount === 0 && !archivedSearchTerm)}
                                    />
                                </div>
                                {isActive && (
                                    <div className="toolbar-right">
                                        <button
                                            className="icon-btn dark-border"
                                            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                                            onClick={() => history.push(Routes.AddPricingTemplate.path)}
                                        >
                                            <Plus size={16} strokeWidth={2} />
                                            Add Pricing Template
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Table — must be inside .table-wrapper */}
                            {data.length === 0 && isActive && !searchTerm ? (
                                <div className="empty-state">
                                    <div className="empty-state-content">
                                        <div className="icon">
                                            <img src={noBrandImage} alt="empty" style={{ width: 120, height: 120 }} />
                                        </div>
                                        <h1 className="no-item-title mt-2">No Pricing Template Exists</h1>
                                        <p className="no-item-text">Nothing here yet. Add your first item to get started!</p>
                                    </div>
                                </div>
                            ) : data.length === 0 && !isActive && !archivedSearchTerm ? (
                                <div className="empty-state">
                                    <div className="empty-state-content">
                                        <div className="icon">
                                            <img src={noBrandImage} alt="empty" style={{ width: 120, height: 120 }} />
                                        </div>
                                        <h1 className="no-item-title mt-2">No Archived Pricing Template Exists</h1>
                                        <p className="no-item-text">Nothing here yet.</p>
                                    </div>
                                </div>
                            ) : data.length === 0 ? (
                                <div className="empty-state">
                                    <div className="empty-state-content">
                                        <div className="icon">
                                            <img src={emptySearchImage} alt="no results" style={{ width: 120, height: 120 }} />
                                        </div>
                                        <h1 className="no-item-title mt-2">{isActive ? 'No Pricing Templates Found' : 'No Archived Pricing Templates Found'}</h1>
                                        <p className="no-item-text">No results match what you're looking for</p>
                                    </div>
                                </div>
                            ) : (
                            <>
                            <style>{`
                                .col-resize-handle::after {
                                    content: "";
                                    position: absolute;
                                    top: 50%;
                                    transform: translateY(-50%);
                                    height: 50%;
                                    left: 3px;
                                    width: 2px;
                                    background: #DEE2E6;
                                }
                            `}</style>
                            <div className="table-wrapper" style={{ border: 'none' }}>
                                <table className="entities-table" style={{ borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed' }}>
                                    <thead height="45px">
                                        <tr>
                                            <SortableHeader
                                                label="Template Name" field="template_name" sortField={activeSortField} sortOrder={activeSortOrder} handleSort={handleSort}
                                                style={{ width: colWidths.templateName, position: 'relative', overflow: 'hidden' }}
                                                resizeHandle={<ColResizeHandle colKey="templateName" minWidth={150} />}
                                            />
                                            {isActive && <SortableHeader label="Status" field="status" sortField={activeSortField} sortOrder={activeSortOrder} handleSort={handleSort} style={{ width: 110, minWidth: 110, maxWidth: 110 }} />}
                                            <th style={{ position: 'relative', overflow: 'hidden', width: colWidths.platformFees }}>
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontFamily: 'Montserrat', fontSize: 12, fontStyle: 'normal', fontWeight: 700, lineHeight: 'normal', color: 'var(--Gray-900, #212529)' }}>
                                                    Platform Fees
                                                </span>
                                                <ColResizeHandle colKey="platformFees" minWidth={200} />
                                            </th>
                                            <th style={{ position: 'relative', overflow: 'hidden', width: colWidths.platformMarkup }}>
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontFamily: 'Montserrat', fontSize: 12, fontStyle: 'normal', fontWeight: 700, lineHeight: 'normal', color: 'var(--Gray-900, #212529)' }}>
                                                    Platform Price Markup
                                                </span>
                                                <ColResizeHandle colKey="platformMarkup" minWidth={200} />
                                            </th>
                                            <SortableHeader
                                                label="Final Result Rule" field="final_result_rule" sortField={activeSortField} sortOrder={activeSortOrder} handleSort={handleSort}
                                                labelSuffix={<FinalResultRuleTooltip />}
                                                style={{ width: colWidths.finalResultRule, position: 'relative', overflow: 'hidden' }}
                                                resizeHandle={<ColResizeHandle colKey="finalResultRule" minWidth={150} />}
                                            />
                                            {isActive
                                                ? <SortableHeader
                                                    label="Last Modified Time" field="modified_at" sortField={activeSortField} sortOrder={activeSortOrder} handleSort={handleSort}
                                                    style={{ width: colWidths.dateCol, position: 'relative', overflow: 'hidden' }}
                                                    resizeHandle={<ColResizeHandle colKey="dateCol" minWidth={120} />}
                                                />
                                                : <SortableHeader
                                                    label="Archived Time" field="archived_at" sortField={activeSortField} sortOrder={activeSortOrder} handleSort={handleSort}
                                                    style={{ width: colWidths.dateCol, position: 'relative', overflow: 'hidden' }}
                                                    resizeHandle={<ColResizeHandle colKey="dateCol" minWidth={120} />}
                                                />
                                            }
                                            <th className="sticky-action-col" style={{ textAlign: 'center', width: 90, minWidth: 90, maxWidth: 90 }}>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.length === 0 ? (
                                            <tr>
                                                <td colSpan={isActive ? 7 : 6} style={{ textAlign: 'center', padding: '40px 12px', color: '#ADB5BD' }}>
                                                    No pricing templates found.
                                                </td>
                                            </tr>
                                        ) : (
                                            data.map((template, idx) => (
                                                <tr
                                                    key={template.pricing_template_id || idx}
                                                    className={[
                                                        idx % 2 ? 'striped' : '',
                                                        (dropdownOpenId === template.pricing_template_id || (drawerOpen && selectedRow?.pricing_template_id === template.pricing_template_id)) ? 'row-selected' : '',
                                                    ].join(' ')}
                                                    onClick={() => openDrawer(template)}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <td style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{template.name}</td>
                                                    {isActive && (
                                                        <td>
                                                            <span className={`status ${template.status?.toLowerCase() === 'active' ? 'active' : 'inactive'}`}>
                                                                {template.status?.toLowerCase() === 'active' ? 'Active' : 'Inactive'}
                                                            </span>
                                                        </td>
                                                    )}
                                                    <td style={{ overflow: 'hidden' }}>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                                            {(template.rows || []).length === 0 ? (
                                                                <span style={{ color: '#ADB5BD' }}>-</span>
                                                            ) : (template.rows || []).map((row, ri) => (
                                                                <div key={ri} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 40, minWidth: 0 }}>
                                                                    <div style={{ color: '#6C757D', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', minWidth: 0 }}>{row.platform_fee_name}</div>
                                                                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                                                        <div style={{ color: '#212529', fontFamily: 'Montserrat', fontSize: 12, fontStyle: 'normal', fontWeight: 600, lineHeight: 'normal' }}>{formatFeeDisplay(row)}</div>
                                                                        {row.formula_type === FEE_PCT_PLATFORM && row.cap && (
                                                                            <div style={{ color: 'var(--Gray-600, #6C757D)', fontFamily: 'var(--Font-Family-Body, Montserrat)', fontSize: 10, fontStyle: 'italic', fontWeight: 400, lineHeight: 'normal' }}>*Cap at SGD {parseFloat(row.cap).toFixed(2)}</div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </td>
                                                    <td style={{ overflow: 'hidden' }}>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                                            {(template.markups || []).length === 0 ? (
                                                                <span style={{ color: '#ADB5BD' }}>-</span>
                                                            ) : (template.markups || []).map((band, bi) => (
                                                                <div key={bi} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 40, minWidth: 0 }}>
                                                                    <div style={{ color: '#6C757D', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', minWidth: 0 }}>{formatMarkupRange(template.markups, bi)}</div>
                                                                    <div style={{ flexShrink: 0, textAlign: 'right', color: '#212529', fontFamily: 'Montserrat', fontSize: 12, fontStyle: 'normal', fontWeight: 600, lineHeight: 'normal' }}>{formatMarkupValue(band)}</div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </td>
                                                    <td style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{finalResultRuleLabel(template.final_result_rule)}</td>
                                                    <td style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{isActive ? (formatDateTime(template.last_modified_time) || '-') : (formatDateTime(template.archived_time) || '-')}</td>
                                                    <td className="sticky-action-col" style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                                                        <button
                                                            ref={(el) => (buttonRefs.current[template.pricing_template_id || idx] = el)}
                                                            className="icon-btn"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                toggleDropdown(template.pricing_template_id || idx);
                                                                setSelectedRow(template);
                                                            }}
                                                        >
                                                            <MoreHorizontal size={20} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            </>
                            )}

                            {/* Per-row dropdown */}
                            {dropdownOpenId !== null && selectedRow && (
                                <div
                                    ref={dropdownRef}
                                    className="backdrop-cls"
                                    style={{
                                        position: 'fixed',
                                        top: dropdownPosition.top - window.scrollY,
                                        left: dropdownPosition.left - window.scrollX,
                                        background: 'white',
                                        boxShadow: '0px 1px 4px 0px #0000001A',
                                        zIndex: 1100,
                                        minWidth: 152,
                                        borderRadius: 8,
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <div className="btn-align-forall">
                                        <div className="common-for-all">
                                            <button onClick={() => openDrawer(selectedRow)}>View Details</button>
                                        </div>
                                        {isActive && (
                                            <>
                                                <div className="common-for-all">
                                                    <button onClick={() => { setDropdownOpenId(null); history.push(`${Routes.AddPricingTemplate.path}?edit=${selectedRow.pricing_template_id}`); }}>Edit</button>
                                                </div>
                                                {selectedRow.status?.toLowerCase() === 'active' ? (
                                                    <div className="common-for-all">
                                                        <button onClick={() => openDeactivateModal(selectedRow)}>Deactivate</button>
                                                    </div>
                                                ) : (
                                                    <div className="common-for-all">
                                                        <button onClick={() => handleActivate(selectedRow)}>Activate</button>
                                                    </div>
                                                )}
                                                <div className="common-for-all">
                                                    <button className="deactivate-cls" onClick={() => openArchiveModal(selectedRow)}>Archive</button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Pagination */}
                            {total > 0 && (
                                <div className="pagination" style={{ marginTop: 16 }}>
                                    <div className="pagination-info">
                                        {`${(page - 1) * PAGE_SIZE + 1} - ${Math.min(page * PAGE_SIZE, total)} of ${total}`}
                                    </div>
                                    <Pagination
                                        currentPage={page}
                                        totalPages={totalPages}
                                        onPageChange={setPage}
                                    />
                                </div>
                            )}

                        </div>
                    </div>
                </div>
            </div>

            {/* Action confirmation modals */}
            <DiscardConfirmationModal
                isOpen={deactivateModal}
                onCancel={() => setDeactivateModal(false)}
                onConfirm={handleDeactivateConfirm}
                entityName="Deactivate Pricing Template"
            />
            <DiscardConfirmationModal
                isOpen={archiveModal}
                onCancel={() => setArchiveModal(false)}
                onConfirm={handleArchiveConfirm}
                entityName="Archive Pricing Template"
            />

            {/* Drawer */}
            {drawerOpen && selectedRow && (
                <>
                    <div className="drawer-overlay" />
                    <div className="entity-drawer" ref={drawerRef}>
                        {/* Fixed header */}
                        <div className="drawer-header" style={{ background: '#F8F9FA' }}>
                            <h3>Pricing Template Info</h3>
                            <button className="close-btn" onClick={closeDrawer}>
                                <X size={24} />
                            </button>
                        </div>

                        {/* Scrollable body */}
                        <div className="main-drawer-inner">
                            {/* Title row: name + Edit + dots (active only) */}
                            <div className="drawer-title d-flex align-items-center justify-content-between" style={{ borderBottom: 'none', paddingBottom: 0 }}>
                                <h2 className="pb-0">{selectedRow.name}</h2>
                                {isActive && (
                                    <div className="entities-tab flex-row align-items-center gap-3">
                                        <div className="edit-organisation-info d-flex justify-content-end align-items-center gap-2 p-0">
                                            <button
                                                className="dark-border"
                                                type="button"
                                                style={{ height: 34, minWidth: 100, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10, border: 'none' }}
                                                onClick={() => history.push(`${Routes.AddPricingTemplate.path}?edit=${selectedRow.pricing_template_id}`)}
                                            >
                                                Edit
                                            </button>
                                        </div>
                                        <div className="toolbar">
                                            <div className="toolbar-right" style={{ position: 'relative' }} ref={drawerDotsRef}>
                                                <button
                                                    className="drawer-dots listing-drawer-action-btn"
                                                    onClick={(e) => { e.stopPropagation(); setDrawerDotsOpen(prev => !prev); }}
                                                >
                                                    <MoreHorizontal size={18} />
                                                </button>
                                                {drawerDotsOpen && (
                                                    <div
                                                        className="backdrop-cls"
                                                        style={{
                                                            position: 'absolute',
                                                            top: 40,
                                                            right: 0,
                                                            background: 'white',
                                                            boxShadow: '0px 1px 4px 0px #0000001A',
                                                            zIndex: 1200,
                                                            minWidth: 152,
                                                            borderRadius: 8,
                                                        }}
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <div className="btn-align-forall">
                                                            {selectedRow.status?.toLowerCase() === 'active' ? (
                                                                <div className="common-for-all">
                                                                    <button onClick={() => openDeactivateModal(selectedRow)}>Deactivate</button>
                                                                </div>
                                                            ) : (
                                                                <div className="common-for-all">
                                                                    <button onClick={() => handleActivate(selectedRow)}>Activate</button>
                                                                </div>
                                                            )}
                                                            <div className="common-for-all">
                                                                <button className="deactivate-cls" onClick={() => openArchiveModal(selectedRow)}>Archive</button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Drawer tabs */}
                            <div className="drawer-tabs" style={{ borderBottom: 'none' }}>
                                <button
                                    className={drawerTab === 'general' ? 'active' : ''}
                                    onClick={() => setDrawerTab('general')}
                                >
                                    General
                                </button>
                                <button
                                    className={drawerTab === 'additional' ? 'active' : ''}
                                    onClick={() => setDrawerTab('additional')}
                                >
                                    Additional
                                </button>
                            </div>

                            {/* Drawer content */}
                            <div className="drawer-content">
                                {drawerTab === 'general' && (
                                    <div className="tab-panel general-panel">

                                        {/* ── Basic Info card ── */}
                                        <div className="card-section" style={{ borderRadius: 12, boxShadow: '0px 4px 12px rgba(0,0,0,0.08)', overflow: 'hidden', marginBottom: 20, marginTop: 15 }}>
                                            <div className="card-header" style={{ backgroundColor: '#F8F9FA', padding: '15px 20px', fontFamily: 'Montserrat', fontSize: 14, fontWeight: 600, color: '#212529', display: 'flex', alignItems: 'center', borderRadius: '12px 12px 0 0', height: 50, borderBottom: 'none' }}>
                                                Basic Info
                                            </div>
                                            <div className="card-body" style={{ backgroundColor: '#fff', padding: 20 }}>

                                                {/* Template Name + Status */}
                                                <div className="field-block" style={{ display: 'grid', gridTemplateColumns: isActive ? '1fr 1fr' : '1fr', gap: 16, borderBottom: 'none', paddingTop: 0 }}>
                                                    <div>
                                                        <div className="field-label">Template Name</div>
                                                        <div className="field-value">{selectedRow.name}</div>
                                                    </div>
                                                    {isActive && (
                                                        <div>
                                                            <div className="field-label">Status</div>
                                                            <div className="field-value">
                                                                <span className={`status ${selectedRow.status?.toLowerCase() === 'active' ? 'active' : 'inactive'}`}>
                                                                    {selectedRow.status?.toLowerCase() === 'active' ? 'Active' : 'Inactive'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Final Result Rule */}
                                                <div className="field-block" style={{ borderBottom: 'none', paddingTop: 0 }}>
                                                    <div className="field-label d-flex align-items-center gap-1">
                                                        Final Result Rule
                                                        <FinalResultRuleTooltip />
                                                    </div>
                                                    <div className="field-value">{finalResultRuleLabel(selectedRow.final_result_rule)}</div>
                                                </div>

                                                {/* Open ID */}
                                                <div className="field-block" style={{ borderBottom: 'none', paddingTop: 0 }}>
                                                    <div className="field-label">Open ID</div>
                                                    <div className="field-value" style={{ gap: 6, flexWrap: 'nowrap', alignItems: 'center' }}>
                                                        <span
                                                            style={{ color: '#0D6EFD', cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 1 }}
                                                            title={selectedRow.open_id}
                                                            onClick={() => handleCopyId(selectedRow.open_id)}
                                                        >
                                                            {selectedRow.open_id}
                                                        </span>
                                                        <span
                                                            style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0, cursor: 'pointer' }}
                                                            onClick={() => handleCopyId(selectedRow.open_id)}
                                                        >
                                                            <Copy size={14} style={{ color: '#0D6EFD' }} />
                                                            {copied && (
                                                                <span style={{ fontSize: 12, color: '#0D6EFD', fontWeight: 500, marginLeft: 4, whiteSpace: 'nowrap' }}>
                                                                    Copied!
                                                                </span>
                                                            )}
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* Created By + Created Time */}
                                                <div className="field-block" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, borderBottom: 'none', paddingTop: 0 }}>
                                                    <div>
                                                        <div className="field-label">Created By</div>
                                                        <div className="field-value">{selectedRow.created_by || <span style={{ color: '#ADB5BD' }}>NA</span>}</div>
                                                    </div>
                                                    <div>
                                                        <div className="field-label">Created Time</div>
                                                        <div className="field-value">{formatDateTime(selectedRow.created_time) || <span style={{ color: '#ADB5BD' }}>NA</span>}</div>
                                                    </div>
                                                </div>

                                                {/* Last Modified By + Last Modified Time */}
                                                <div className="field-block" style={{ borderBottom: 'none', paddingTop: 0, paddingBottom: 0, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                                    <div>
                                                        <div className="field-label">Last Modified By</div>
                                                        <div className="field-value">{selectedRow.last_modified_by || <span style={{ color: '#ADB5BD' }}>NA</span>}</div>
                                                    </div>
                                                    <div>
                                                        <div className="field-label">Last Modified Time</div>
                                                        <div className="field-value">{formatDateTime(selectedRow.last_modified_time) || <span style={{ color: '#ADB5BD' }}>NA</span>}</div>
                                                    </div>
                                                </div>

                                                {/* Archived By + Archived Time — only for archived rows */}
                                                {!isActive && (
                                                    <div className="field-block" style={{ borderBottom: 'none', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                                        <div>
                                                            <div className="field-label">Archived By</div>
                                                            <div className="field-value">{selectedRow.archived_by || <span style={{ color: '#ADB5BD' }}>NA</span>}</div>
                                                        </div>
                                                        <div>
                                                            <div className="field-label">Archived Time</div>
                                                            <div className="field-value">{formatDateTime(selectedRow.archived_time) || <span style={{ color: '#ADB5BD' }}>NA</span>}</div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* ── Formula Info card ── */}
                                        <div className="card-section" style={{ borderRadius: 12, boxShadow: '0px 4px 12px rgba(0,0,0,0.08)', overflow: 'hidden', marginBottom: 20 }}>
                                            <div className="card-header" style={{ backgroundColor: '#F8F9FA', padding: '15px 20px', fontFamily: 'Montserrat', fontSize: 14, fontWeight: 600, color: '#212529', display: 'flex', alignItems: 'center', borderRadius: '12px 12px 0 0', height: 50, borderBottom: 'none' }}>
                                                Formula Info
                                            </div>
                                            <div className="card-body" style={{ backgroundColor: '#fff', padding: 20 }}>
                                                {(selectedRow.rows || []).length === 0 ? (
                                                    <div className="field-block" style={{ borderBottom: 'none', paddingTop: 0, paddingBottom: 0 }}>
                                                        <div className="field-value" style={{ color: '#ADB5BD' }}>-</div>
                                                    </div>
                                                ) : (() => {
                                                    const rows = selectedRow.rows || [];
                                                    const pairs = [];
                                                    for (let i = 0; i < rows.length; i += 2) pairs.push(rows.slice(i, i + 2));
                                                    return pairs.map((pair, pi) => (
                                                        <div key={pi} className="field-block" style={{ display: 'grid', gridTemplateColumns: pair.length === 2 ? '1fr 1fr' : '1fr', gap: 16, borderBottom: 'none', paddingTop: 0, ...(pi === pairs.length - 1 ? { paddingBottom: 0 } : {}) }}>
                                                            {pair.map((row, ri) => (
                                                                <div key={ri}>
                                                                    <div className="field-label">{row.platform_fee_name}</div>
                                                                    <div className="field-value" style={{ flexWrap: 'wrap', gap: 4 }}>
                                                                        <span>{formatFeeDisplay(row)}</span>
                                                                        {row.formula_type === FEE_PCT_PLATFORM && row.cap && (
                                                                            <span style={{ color: '#6C757D', fontSize: 11, fontStyle: 'italic' }}>*Cap at SGD {parseFloat(row.cap).toFixed(2)}</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ));
                                                })()}
                                            </div>
                                        </div>

                                        {/* ── Platform Price Markup card ── */}
                                        <div className="card-section" style={{ borderRadius: 12, boxShadow: '0px 4px 12px rgba(0,0,0,0.08)', overflow: 'hidden', marginBottom: 20 }}>
                                            <div className="card-header" style={{ backgroundColor: '#F8F9FA', padding: '15px 20px', fontFamily: 'Montserrat', fontSize: 14, fontWeight: 600, color: '#212529', display: 'flex', alignItems: 'center', borderRadius: '12px 12px 0 0', height: 50, borderBottom: 'none' }}>
                                                Platform Price Markup
                                            </div>
                                            <div className="card-body" style={{ backgroundColor: '#fff', padding: 20 }}>
                                                {(selectedRow.markups || []).length === 0 ? (
                                                    <div className="field-block" style={{ borderBottom: 'none', paddingTop: 0, paddingBottom: 0 }}>
                                                        <div className="field-value" style={{ color: '#ADB5BD' }}>-</div>
                                                    </div>
                                                ) : (selectedRow.markups || []).map((band, bi, all) => (
                                                    <div
                                                        key={bi}
                                                        className="field-block"
                                                        style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, borderBottom: 'none', paddingTop: 0, ...(bi === all.length - 1 ? { paddingBottom: 0 } : {}) }}
                                                    >
                                                        <div>
                                                            <div className="field-label">Markup Range</div>
                                                            <div className="field-value">{formatMarkupRange(all, bi)}</div>
                                                        </div>
                                                        <div>
                                                            <div className="field-label">Markup</div>
                                                            <div className="field-value">{formatMarkupValue(band)}</div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                    </div>
                                )}

                                {drawerTab === 'additional' && (
                                    <div className="tab-panel">
                                        <div
                                            className="card-section"
                                            style={{
                                                borderRadius: 12,
                                                boxShadow: '0px 4px 12px rgba(0,0,0,0.08)',
                                                overflow: 'hidden',
                                                marginBottom: 20,
                                                marginTop: 15,
                                            }}
                                        >
                                            <div
                                                className="card-header"
                                                style={{
                                                    backgroundColor: '#F8F9FA',
                                                    padding: '15px 20px',
                                                    fontFamily: 'Montserrat',
                                                    fontSize: 14,
                                                    fontWeight: 600,
                                                    color: '#212529',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    borderRadius: '12px 12px 0 0',
                                                    height: 50,
                                                    borderBottom: 'none',
                                                }}
                                            >
                                                Notes
                                            </div>
                                            <div className="card-body" style={{ backgroundColor: '#fff', padding: 16 }}>
                                                <p style={{ fontFamily: 'Montserrat', fontSize: 14, color: '#212529', margin: 0, lineHeight: '22px', whiteSpace: 'pre-line' }}>
                                                    {selectedRow.notes || '-'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
